import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  value: string | string[];
  onChange: (url: string | string[]) => void;
  defaultArticles?: string[];
  multipleUrls?: boolean;
}

export const ArticleUrlInput: React.FC<Props> = ({ value, onChange, multipleUrls = false }) => {
  const { t } = useTranslation();

  // Handle single URL mode (backward compatibility)
  if (!multipleUrls) {
    return (
      <>
        <label className="block text-gray-700">{t('ads.article_url')}</label>
        <input
          type="url"
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 w-full p-2 border rounded-md"
          placeholder={t('ads.article_url_placeholder')}
        />
      </>
    );
  }

  // Handle multiple URLs mode
  const urls = Array.isArray(value) ? value : [value as string];
  const [visibleInputs, setVisibleInputs] = React.useState(
    Math.max(
      1,
      urls.findIndex((url) => url === '') === -1 ? urls.length : urls.findIndex((url) => url === '')
    )
  );

  const handleUrlChange = (index: number, newUrl: string) => {
    const newUrls = [...urls];
    newUrls[index] = newUrl;
    // Remove empty URLs from the end
    while (newUrls.length > 1 && newUrls[newUrls.length - 1] === '') {
      newUrls.pop();
    }
    onChange(newUrls);
  };

  const removeUrl = (index: number) => {
    if (visibleInputs > 1) {
      const newUrls = urls.filter((_, i) => i !== index);
      setVisibleInputs(visibleInputs - 1);
      onChange(newUrls.length > 0 ? newUrls : ['']);
    }
  };

  const addUrlInput = () => {
    if (visibleInputs < 3) {
      setVisibleInputs(visibleInputs + 1);
      if (urls.length === visibleInputs) {
        onChange([...urls, '']);
      }
    }
  };

  const renderUrlInput = (index: number) => (
    <div key={index} className={`${index === 0 ? 'mt-2' : 'mt-3'} relative`}>
      <input
        type="url"
        value={urls[index] || ''}
        onChange={(e) => handleUrlChange(index, e.target.value)}
        className="w-full p-2 border rounded-md pr-10"
        placeholder={t('newsletter.article_url_placeholder')}
      />
      {visibleInputs > 1 && (
        <button
          type="button"
          onClick={() => removeUrl(index)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 w-6 h-6 flex items-center justify-center text-lg font-bold"
          aria-label={`Remove URL ${index + 1}`}
        >
          ×
        </button>
      )}
    </div>
  );

  const renderAddButton = (nextIndex: number) => {
    const buttonTextKey =
      nextIndex === 2 ? 'newsletter.add_second_url' : 'newsletter.add_third_url';

    return (
      <button
        key={`add-${nextIndex}`}
        type="button"
        onClick={addUrlInput}
        className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
      >
        {t(buttonTextKey)}
      </button>
    );
  };

  return (
    <>
      <label className="block text-gray-700">{t('newsletter.article_url')}</label>

      {/* Render visible URL inputs */}
      {Array.from({ length: visibleInputs }, (_, i) => renderUrlInput(i))}

      {/* Render add button if we can show more inputs */}
      {visibleInputs < 3 && renderAddButton(visibleInputs + 1)}
    </>
  );
};
